import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import liveTalkIcon from 'assets/DetailImage/liveTalk.png';
import defaultProfileImage from 'assets/MemberImage/defaultProfileImage.png';
import { facilityService, LiveTalk } from 'services/facilityService';
import { memberService } from 'services/memberService';
import { Building } from 'types';
import {
  Container,
  Header,
  BackButton,
  HeaderTitle,
  HeaderIcon,
  FacilityInfo,
  FacilityName,
  TalkList,
  TalkItem,
  TalkHeader,
  ProfileImage,
  UserInfo,
  Username,
  Timestamp,
  TalkContent,
  LoadingContainer,
  EmptyContainer,
  EmptyText,
  LoadMoreTrigger,
  InputContainer,
  TextInput,
  SendButton,
  ContentWrapper
} from './LiveTalkList.styles';

const PAGE_SIZE = 10;

function LiveTalkList() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [building, setBuilding] = useState<Building | null>(null);
  const [talks, setTalks] = useState<LiveTalk[]>([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return '방금 전';
    if (diffMinutes < 60) return `${diffMinutes}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return date.toLocaleDateString('ko-KR');
  };

  // Fetch building info
  useEffect(() => {
    const fetchBuilding = async () => {
      if (!id) return;
      try {
        const data = await memberService.getBuilding(Number(id));
        setBuilding(data);
      } catch (error) {
        console.error('Error fetching building:', error);
      }
    };
    fetchBuilding();
  }, [id]);

  // Fetch initial talks
  useEffect(() => {
    const fetchTalks = async () => {
      if (!id) return;
      try {
        setIsLoading(true);
        const response = await facilityService.getLiveTalk(Number(id), 0, PAGE_SIZE);
        setTalks(response.content);
        setHasMore(!response.last);
        setPage(0);
      } catch (error) {
        console.error('Error fetching talks:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchTalks();
  }, [id]);

  // Load more talks
  const loadMore = useCallback(async () => {
    if (!id || isLoadingMore || !hasMore) return;

    try {
      setIsLoadingMore(true);
      const nextPage = page + 1;
      const response = await facilityService.getLiveTalk(Number(id), nextPage, PAGE_SIZE);
      setTalks(prev => [...prev, ...response.content]);
      setHasMore(!response.last);
      setPage(nextPage);
    } catch (error) {
      console.error('Error loading more talks:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [id, page, isLoadingMore, hasMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (isLoading) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [isLoading, hasMore, isLoadingMore, loadMore]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleSendTalk = async () => {
    if (!inputValue.trim() || isSending || !id) return;

    try {
      setIsSending(true);

      // 톡 생성 API 호출
      await facilityService.createLiveTalk(Number(id), inputValue.trim());

      // 입력창 초기화
      setInputValue('');
      inputRef.current?.focus();

      // 목록 새로고침
      const response = await facilityService.getLiveTalk(Number(id), 0, PAGE_SIZE);
      setTalks(response.content);
      setHasMore(!response.last);
      setPage(0);
    } catch (error) {
      console.error('Error sending talk:', error);
      alert('톡 전송에 실패했습니다.');
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendTalk();
    }
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={handleGoBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </BackButton>
        <HeaderTitle>
          실시간 톡
          <HeaderIcon src={liveTalkIcon} alt="live talk" />
        </HeaderTitle>
      </Header>

      {building && (
        <FacilityInfo>
          <FacilityName>{building.name}</FacilityName>
        </FacilityInfo>
      )}

      <ContentWrapper>
        {isLoading ? (
          <LoadingContainer>로딩 중...</LoadingContainer>
        ) : talks.length === 0 ? (
          <EmptyContainer>
            <HeaderIcon src={liveTalkIcon} alt="empty" style={{ width: 48, height: 48, opacity: 0.5 }} />
            <EmptyText>아직 작성된 톡이 없습니다.</EmptyText>
            <EmptyText style={{ fontSize: 13, color: '#999' }}>첫 번째 톡의 주인공이 되어보세요!</EmptyText>
          </EmptyContainer>
        ) : (
          <TalkList>
            {talks.map((talk) => (
              <TalkItem key={talk.id}>
                <TalkHeader>
                  <ProfileImage
                    src={talk.member.profileImage || defaultProfileImage}
                    alt={talk.member.name}
                    referrerPolicy="no-referrer"
                  />
                  <UserInfo>
                    <Username>{talk.member.name}</Username>
                    <Timestamp>{formatTimeAgo(talk.createdAt)}</Timestamp>
                  </UserInfo>
                </TalkHeader>
                <TalkContent>{talk.message}</TalkContent>
              </TalkItem>
            ))}

            <LoadMoreTrigger ref={loadMoreRef} />

            {isLoadingMore && (
              <LoadingContainer>더 불러오는 중...</LoadingContainer>
            )}
          </TalkList>
        )}
      </ContentWrapper>

      <InputContainer>
        <TextInput
          ref={inputRef}
          type="text"
          placeholder="톡을 입력해주세요"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isSending}
        />
        <SendButton
          onClick={handleSendTalk}
          disabled={!inputValue.trim() || isSending}
        >
          <FontAwesomeIcon icon={faPaperPlane} />
        </SendButton>
      </InputContainer>
    </Container>
  );
}

export default LiveTalkList;
